import React, { useCallback, useEffect } from 'react';
import {
  defineMessages,
  FormattedMessage,
  useIntl,
  type MessageValue,
  type NoMessageValues,
} from 'react-intl';
import { StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { theme } from '@garnish/constants';
import { BodyText, Button, useResponsive } from '@garnish/core';
import { getCloudinaryUrl, Image } from '@garnish/media';
import { Modal } from '@garnish/overlays';

import { INGREDIENT_CARD_IMAGE_WIDTH } from '../../../constants';
import { CustomizationModalContentContainer } from '../CustomizationModalContentContainer';
import { CustomizationModalHeading } from '../CustomizationModalHeading';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationOutOfStockModalV2 = (props: Props) => {
  const {
    isVisible,
    outOfStockIngredients,
    productName,
    headingEnd,
    chooseReplacementButtonTitle,
    size = 'md',
    onShow,
    onRequestClose,
    onChooseNewIngredientPress,
  } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  const formattedHeadingEnd = headingEnd
    ? headingEnd.replaceAll(DYNAMIC_PRODUCT_TEXT, productName)
    : formatMessage(messages.headingEnd, { product: productName });

  const formattedChooseReplacementButtonTitle =
    chooseReplacementButtonTitle ?? formatMessage(messages.chooseReplacement);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleCta = useCallback(() => {
    const firstIngredient = outOfStockIngredients[0];
    const groupId = firstIngredient.modificationGroupId;

    onRequestClose();
    onChooseNewIngredientPress(groupId);
  }, [outOfStockIngredients, onRequestClose, onChooseNewIngredientPress]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isVisible) return;

    onShow?.();
  }, [isVisible, onShow]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      size="medium"
      visible={isVisible}
      accessibilityLabel={formatMessage(messages.outOfStockA11y)}
      onRequestClose={onRequestClose}
    >
      <SafeAreaView edges={match([['bottom'], undefined])}>
        <CustomizationModalContentContainer size={size}>
          {outOfStockIngredients.length > 0 ? (
            <CustomizationModalHeading>
              {outOfStockIngredients.map((modification, index) => {
                const { ingredient } = modification;

                if (!ingredient) return null;

                const hasMultipleIngredients = outOfStockIngredients.length > 1;
                const isLastIngredient = index === outOfStockIngredients.length - 1; // prettier-ignore

                const shouldAddAnd = hasMultipleIngredients && isLastIngredient;
                const shouldAddComma = hasMultipleIngredients && index < outOfStockIngredients.length - 2; // prettier-ignore

                return (
                  <CustomizationModalHeading key={ingredient.id}>
                    {shouldAddAnd ? (
                      <>
                        <FormattedMessage {...messages.and} />
                        <Text> </Text>
                      </>
                    ) : null}

                    <CustomizationModalHeading bold>
                      {ingredient.name}
                      <Text>{shouldAddComma ? ', ' : ' '}</Text>
                    </CustomizationModalHeading>
                  </CustomizationModalHeading>
                );
              })}

              <CustomizationModalHeading>{formattedHeadingEnd}</CustomizationModalHeading>
            </CustomizationModalHeading>
          ) : null}

          <View style={styles.ingredientsContainer}>
            {outOfStockIngredients.map((modification) => {
              const { ingredient } = modification;

              if (!ingredient) return null;

              return (
                <IngredientCard
                  key={ingredient.id}
                  name={ingredient.name}
                  assetUrl={ingredient.asset.url}
                />
              );
            })}
          </View>

          <View style={styles.ctaContainer}>
            <Button
              style={styles.button}
              size="large-wide"
              palette="spinachMuted"
              onPress={handleCta}
            >
              {formattedChooseReplacementButtonTitle}
            </Button>
          </View>
        </CustomizationModalContentContainer>
      </SafeAreaView>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const IngredientCard = (props: CardProps) => {
  const { name, assetUrl } = props;

  const ingredientImage = getCloudinaryUrl(assetUrl, {
    crop: 'scale',
    width: INGREDIENT_CARD_IMAGE_WIDTH,
  });

  return (
    <View style={styles.ingredient}>
      <Image source={ingredientImage} style={styles.image} alt={name} />
      <BodyText sizeMatch={['20']} style={styles.ingredientName}>
        {name}
      </BodyText>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const IMAGE_SIZE = 40;
const DYNAMIC_PRODUCT_TEXT = '(product)';

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly headingEnd: { readonly product: MessageValue };
  readonly and: NoMessageValues;
  readonly outOfStockA11y: NoMessageValues;
  readonly chooseReplacement: NoMessageValues;
}>({
  headingEnd: {
    defaultMessage: 'is typically in the {product}, but is currently out of stock.',
    description: 'Customization | Out of Stock | Heading end',
  },
  and: {
    defaultMessage: 'and',
    description: 'Customization | Out of Stock | And',
  },
  outOfStockA11y: {
    defaultMessage: 'Out of Stock',
    description: 'Customization | Out of Stock | A11y',
  },
  chooseReplacement: {
    defaultMessage: 'Choose replacement',
    description: 'Customization | Out of Stock | Choose Replacement',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ctaContainer: {
    alignItems: 'center',
    paddingTop: theme.spacing['2'],
  },
  ingredientsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing['6'],
    paddingBottom: theme.spacing['2'],
  },
  ingredient: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
  button: {
    minWidth: 260,
    height: 72,
  },
  image: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    borderRadius: IMAGE_SIZE,
    backgroundColor: theme.colors.LIGHT,
  },
  ingredientName: {
    color: theme.colors.KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isVisible: boolean;
  outOfStockIngredients: {
    modificationGroupId: string;
    ingredient?: { id: string; name: string; asset: { url: string } } | null;
  }[];
  productName: string;
  headingEnd?: string;
  chooseReplacementButtonTitle?: string;
  size?: 'md' | 'lg';
  onShow?: () => void;
  onRequestClose: () => void;
  onChooseNewIngredientPress: (groupId: string) => void;
};

type CardProps = {
  name: string;
  assetUrl: string;
};
